import classNames from '@utils/classnames';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { ErrorStateBoundary } from '../error-state/ErrorStateBoundary';

export interface ITabItem {
  title: string;
  /** Absolute (`/internal/orders/1/general`) or route-relative (`general`) path. */
  path: string;
  /** Optional counter shown as a small grey pill next to the title. */
  count?: number;
}

export interface ITabBarProps {
  items: ITabItem[];
  actions?: React.ReactNode;
}

/**
 * Just the tab strip. Use `Tabs` unless the tab panels can't live in an
 * `<Outlet />` right below the strip — e.g. a `ListPage` that needs the strip in
 * its heading and the table in its full-height body.
 */
export const TabBar: React.FC<ITabBarProps> = (props) => {
  const { items, actions } = props;
  const { search } = useLocation();

  return (
    <div className="flex items-center justify-between gap-4 border-b border-border">
      <div className="flex flex-1 gap-1 overflow-x-auto">
        {items.map((item) => (
          <NavLink
            to={item.path + search}
            key={`tab-${item.path}`}
            className={({ isActive }) =>
              classNames(
                '-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'border-brand font-semibold text-fg'
                  : 'border-transparent font-medium text-fg-secondary hover:border-border-strong hover:text-fg',
              )
            }
          >
            {item.title}
            {item.count != null && (
              <span className="rounded-full bg-neutral-subtle px-1.5 py-px text-2xs font-semibold leading-4 text-fg-muted">
                {item.count}
              </span>
            )}
          </NavLink>
        ))}
      </div>
      {actions && <div className="shrink-0 pb-1.5">{actions}</div>}
    </div>
  );
};

export interface ITabsProps extends ITabBarProps {}

/**
 * Route-driven tabs. One tab style app-wide: brand underline on the active tab,
 * grey underline on hover. Active matching uses the router's own resolution,
 * so relative and absolute paths both work.
 */
export const Tabs: React.FC<ITabsProps> = (props) => {
  const { items, actions } = props;

  return (
    <div>
      <TabBar items={items} actions={actions} />

      <div className="pt-4">
        <ErrorStateBoundary variant="section">
          <Outlet />
        </ErrorStateBoundary>
      </div>
    </div>
  );
};
